import { ReportHandler, Metric } from "web-vitals";
import { trackPerformanceMetric } from "./utils/performance";
import { logger } from "./utils/logger";

/**
 * Get PostHog instance for analytics
 */
function getPostHog(): unknown {
  try {
    if (typeof window !== "undefined") {
      const win = window as unknown as { posthog?: unknown };
      if (win.posthog) {
        return win.posthog;
      }
    }
  } catch {
    // Ignore errors
  }
  return null;
}

/**
 * Report web vitals to PostHog and console (dev only)
 */
function reportToAnalytics(metric: Metric) {
  const posthog = getPostHog();

  // Extract optional properties safely
  const rating =
    "rating" in metric
      ? (metric as Metric & { rating?: string }).rating
      : undefined;
  const navigationType =
    "navigationType" in metric
      ? (metric as Metric & { navigationType?: string }).navigationType
      : undefined;

  // Send to PostHog
  if (
    posthog &&
    typeof posthog === "object" &&
    "capture" in posthog &&
    typeof (posthog as { capture: unknown }).capture === "function"
  ) {
    (
      posthog as {
        capture: (event: string, props: Record<string, unknown>) => void;
      }
    ).capture("web_vital", {
      metric_name: metric.name,
      metric_value: metric.value,
      metric_id: metric.id,
      ...(rating && { metric_rating: rating }),
      ...(navigationType && { metric_navigationType: navigationType }),
    });
  }

  // Track as performance metric
  trackPerformanceMetric({
    name: `web_vital_${metric.name.toLowerCase()}`,
    value: metric.value,
    tags: {
      ...(rating && { rating }),
      ...(navigationType && { navigationType }),
      id: metric.id,
    },
  });

  // Log in development
  if (import.meta.env.DEV) {
    logger.debug(`[Web Vitals] ${metric.name}`, {
      value: metric.value,
      ...(rating && { rating }),
      id: metric.id,
      ...(navigationType && { navigationType }),
    });
  }
}

const reportWebVitals = (onPerfEntry?: ReportHandler) => {
  import("web-vitals").then(({ getCLS, getFID, getFCP, getLCP, getTTFB }) => {
    // Always report to analytics
    getCLS(reportToAnalytics);
    getFCP(reportToAnalytics);
    getLCP(reportToAnalytics);
    getTTFB(reportToAnalytics);
    getFID(reportToAnalytics);

    // Also call custom handler if provided
    if (onPerfEntry && onPerfEntry instanceof Function) {
      getCLS(onPerfEntry);
      getFCP(onPerfEntry);
      getLCP(onPerfEntry);
      getTTFB(onPerfEntry);
      getFID(onPerfEntry);
    }
  });
};

export default reportWebVitals;
